// KoRichTextEditor.jsx
import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { createEditor, Transforms, Range, Editor } from "slate";
import { Slate, Editable, withReact, ReactEditor } from "slate-react";
import { withHistory } from "slate-history";

import { Toolbar } from "./toolbar/toolbar";
import { Element } from "./elements/element";
import { Leaf } from "./elements/leaf";

import styles from "./ac.module.scss";

import withLayout from "./utils/withlayout";
import withTables from "./utils/withtable";
import withImages from "./utils/withImages";

import HoveringToolbar from "./toolbar/buttons/floatingToolbar";
import KoLoader from "packages/loader";

import { uploadImageAndInsert } from "./utils/uploadImageAndInsert";
import { uploadVideoFile } from "./utils/uploadVideoFile";
import { toggleMark, withMentionsAndTags } from "./utils/slateUtils";
import MentionElement from "./elements/mentionElement";
import TagElement from "./elements/tagElement";

const SAMPLE_USERS = [
  { id: "u1", name: "Menthon Dev", avatar: "" },
  { id: "u2", name: "Kamal K", avatar: "" },
  { id: "u3", name: "Harshini P", avatar: "" },
  { id: "u4", name: "Ravi P", avatar: "" }
];

const SAMPLE_TAGS = ["bug", "feature", "urgent", "backend", "frontend", "release"];

const KoRichTextEditor = ({
  initialValues,
  isFeed,
  isToolbar = true,
  onSetEditorValues,
  placeholder = "",
  onUpdateAttachment,
  attachments,
  lookupId,
  workspaceId,
  onfeedComments=null,
  enabledButtons = null,
  commentId
}) => {
  const editor = useMemo(
    () =>
      withMentionsAndTags(
        withLayout(withTables(withHistory(withImages(withReact(createEditor())))))
      ),
    []
  );

  const initial = initialValues || [
    {
      type: "paragraph",
      children: [{ text: "" }]
    }
  ];
  const [value, setValue] = useState(initial);

  const [loading, setLoading] = useState(false);
  const [targetRange, setTargetRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [mode, setMode] = useState(null);
  const [filtered, setFiltered] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const popupRef = useRef();
  const editorRef = useRef();

  const renderElement = useCallback(
    (props) => {
      const { element } = props;
      if (element.type === "mention") return <MentionElement {...props} />;
      if (element.type === "tag") return <TagElement {...props} />;
      return <Element attachments={attachments} {...props} />;
    },
    [attachments]
  );

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  function closePopup() {
    setMode(null);
    setTargetRange(null);
    setSearchText("");
    setFiltered([]);
    setActiveIndex(0);
  }

  useEffect(() => {
    if (!mode) {
      setFiltered([]);
      return;
    }
    const q = (searchText || "").toLowerCase();
    if (mode === "mention") {
      setFiltered(SAMPLE_USERS.filter((u) => u.name.toLowerCase().includes(q)));
      setActiveIndex(0);
    } else {
      setFiltered(SAMPLE_TAGS.filter((t) => t.toLowerCase().includes(q)));
      setActiveIndex(0);
    }
  }, [mode, searchText]);

  const getTokenRangeAtCursor = (editor) => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) return null;

    let start = selection.anchor;
    let prev = Editor.before(editor, start, { unit: "character" });

    while (prev) {
      const r = Editor.range(editor, prev, start);
      const ch = Editor.string(editor, r);
      if (!ch || /\s/.test(ch)) break;
      start = prev;
      prev = Editor.before(editor, start, { unit: "character" });
    }

    const range = Editor.range(editor, start, selection.anchor);
    return range;
  };

  const detectTrigger = () => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
      closePopup();
      return;
    }

    const tokenRange = getTokenRangeAtCursor(editor);
    if (!tokenRange) {
      closePopup();
      return;
    }

    const tokenText = Editor.string(editor, tokenRange);
    if (!tokenText) {
      closePopup();
      return;
    }

    if (tokenText.startsWith("@")) {
      setMode("mention");
      setSearchText(tokenText.slice(1));
      setTargetRange(tokenRange);
      positionPopup(tokenRange);
      return;
    }

    if (tokenText.startsWith("#")) {
      setMode("tag");
      setSearchText(tokenText.slice(1));
      setTargetRange(tokenRange);
      positionPopup(tokenRange);
      return;
    }

    closePopup();
  };

  function positionPopup(range) {
    try {
      const domRange = ReactEditor.toDOMRange(editor, range);
      let rect = domRange.getBoundingClientRect();

      if (rect.x === 0 && rect.y === 0) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const fallback = selection.getRangeAt(0).getBoundingClientRect();
          if (fallback && fallback.width !== 0) rect = fallback;
        }
      }

      const editorRect = editorRef.current.getBoundingClientRect();
      const top = rect.bottom - editorRect.top;
      const left = rect.left - editorRect.left;

      popupRef.current.style.top = `${top + 8}px`;
      popupRef.current.style.left = `${left}px`;
    } catch {}
  }

  const insertMention = (user) => {
    if (!targetRange) return closePopup();
    Transforms.select(editor, targetRange);

    const mentionNode = {
      type: "mention",
      userId: user.id,
      name: user.name,
      children: [{ text: "" }]
    };

    Transforms.insertNodes(editor, mentionNode);
    Transforms.move(editor, { distance: 1, unit: "character" });
    ReactEditor.focus(editor);
    closePopup();
  };

  const insertTag = (tag) => {
    if (!targetRange) return closePopup();
    Transforms.select(editor, targetRange);

    const tagNode = {
      type: "tag",
      tag,
      children: [{ text: "" }]
    };

    Transforms.insertNodes(editor, tagNode);
    Transforms.move(editor, { distance: 1, unit: "character" });
    ReactEditor.focus(editor);
    closePopup();
  };

  const onKeyDown = (event) => {
    if (mode) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        closePopup();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const item = filtered[activeIndex];
        if (!item) return closePopup();
        if (mode === "mention") insertMention(item);
        if (mode === "tag") insertTag(item);
        return;
      }
    }

    if (event.key === "Enter" && !mode) {
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const tokenRange = getTokenRangeAtCursor(editor);
        const text = tokenRange && Editor.string(editor, tokenRange);

        if (text && text.startsWith("@") && text.length > 1) {
          event.preventDefault();
          const name = text.slice(1);
          insertMention({ id: name.toLowerCase(), name });
          return;
        }

        if (text && text.startsWith("#") && text.length > 1) {
          event.preventDefault();
          const tag = text.slice(1);
          insertTag(tag);
          return;
        }
      }
    }

    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
      case "b":
        event.preventDefault();
        toggleMark(editor, "bold");
        break;
      case "i":
        event.preventDefault();
        toggleMark(editor, "italic");
        break;
      case "u":
        event.preventDefault();
        toggleMark(editor, "underline");
        break;
      case "`":
        event.preventDefault();
        toggleMark(editor, "code");
        break;
      default:
        break;
      }
    }
  };

  const onChangeInternal = (val) => {
    setValue(val);
    onfeedComments && onfeedComments(val);
    if (typeof onSetEditorValues === "function") onSetEditorValues(val);

    requestAnimationFrame(() => {
      detectTrigger();
    });
  };

  useEffect(() => {
    const handler = () => {
      if (mode && targetRange) positionPopup(targetRange);
    };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [mode, targetRange]);

  const onPaste = async(event) => {
    const items = event.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await uploadImageAndInsert(
            editor,
            file,
            onUpdateAttachment,
            lookupId,
            workspaceId,
            commentId,
            setLoading
          );
        }
        return;
      }
    }
  };

  const onDrop = async(event) => {
    event.preventDefault();
    const data = event.dataTransfer?.getData("application/x-slate-fragment");

    const files = Array.from(event.dataTransfer.files || []);
    for (const file of files) {
      if (file.type.startsWith("video/"))
        await uploadVideoFile(
          editor,
          file,
          onUpdateAttachment,
          lookupId,
          workspaceId,
          commentId,
          setLoading
        );

      if (file.type.startsWith("image/"))
        await uploadImageAndInsert(
          editor,
          file,
          onUpdateAttachment,
          lookupId,
          workspaceId,
          commentId,
          setLoading
        );
    }

    if (data) {
      try {
        const node = JSON.parse(data);
        const range = ReactEditor.findEventRange(editor, event);
        if (range) {
          Transforms.select(editor, range.focus);
        }
        Transforms.insertNodes(editor, node);
      } catch {}
    }
  };

  return (
    <div>
      {loading && <KoLoader />}

      <Slate editor={editor} value={value} initialValue={initial} onChange={onChangeInternal}>
        {isToolbar && (
          <Toolbar
            lookupId={lookupId}
            commentId={commentId}
            workspaceId={workspaceId}
            className={`${styles.toolbar}`}
            enabledButtons={enabledButtons}
            editor={editor}
            onUpdateAttachment={onUpdateAttachment}
          />
        )}

        <div
          className={`${styles.card} richTextWrapper ${isFeed ? styles.feed : ""}`}
          ref={editorRef}
        >
          <HoveringToolbar editor={editor} />

          <Editable
            className={styles.editableWrapper}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            spellCheck
            onPaste={onPaste}
            onDrop={onDrop}
            onKeyDown={onKeyDown}
            onBlur={() => {
              setTimeout(() => {
                const domSel = window.getSelection();
                if (!domSel || !domSel.anchorNode) {
                  closePopup();
                  return;
                }
                if (editorRef.current && editorRef.current.contains(domSel.anchorNode)) {
                  return;
                }
                closePopup();
              }, 150);
            }}
          />

          {mode && filtered.length > 0 && (
            <div
              ref={popupRef}
              className={styles.mentionPopup}
              style={{
                position: "absolute",
                zIndex: 2000,
                minWidth: 220,
                maxHeight: 260,
                overflow: "auto",
                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                borderRadius: 8,
                background: "#fff",
                padding: 6
              }}
            >
              <div style={{ padding: "6px 8px" }}>
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={mode === "mention" ? "Search people..." : "Search tags..."}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #eee",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div>
                {filtered.map((item, idx) => {
                  const active = idx === activeIndex;
                  return (
                    <div
                      key={mode === "mention" ? item.id : item}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (mode === "mention") insertMention(item);
                        else insertTag(item);
                      }}
                      onMouseEnter={() => setActiveIndex(idx)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        cursor: "pointer",
                        background: active ? "#f0f6ff" : "white",
                        borderRadius: 6
                      }}
                    >
                      {mode === "mention" ? (
                        <>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 16,
                              background: "#ddd",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700
                            }}
                          >
                            {item.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{item.name}</div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              @{item.name.replace(/\s+/g, "").toLowerCase()}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontWeight: 600 }}>#{item}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Slate>
    </div>
  );
};

export default KoRichTextEditor;
